import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import HintButton from "@/components/HintButton";
import KpiGrid from "@/components/production/KpiGrid";
import FiltersSidebar from "@/components/production/FiltersSidebar";
import OverviewTab from "@/components/production/OverviewTab";
import StationsTab from "@/components/production/StationsTab";
import CollaboratorsTab from "@/components/production/CollaboratorsTab";
import SkusTab from "@/components/production/SkusTab";
import RankingsTab from "@/components/production/RankingsTab";
import { buildParams, downloadBlob } from "@/components/production/pcShared";
import {
  ChartPieSlice, Factory, Users, Barcode, Trophy, FunnelSimple,
  FileXls, FileCsv, FilePdf, FileCode, Printer,
} from "@phosphor-icons/react";

const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
};
const todayLocal = () => new Date().toLocaleDateString("en-CA");

const EMPTY_FILTERS = () => ({
  date_from: monthStart(), date_to: todayLocal(),
  collaborators: [], sectors: [], postos: [], statuses: [], types: [],
  marketplaces: [], stores: [], states: [], colors: [],
  skus: "", cities: "", order_number: "",
  qty_min: "", qty_max: "", value_min: "", value_max: "",
});

const TABS = [
  { key: "overview", label: "Visão Geral", icon: ChartPieSlice },
  { key: "stations", label: "Postos & Etapas", icon: Factory },
  { key: "collaborators", label: "Colaboradores", icon: Users },
  { key: "skus", label: "SKUs", icon: Barcode },
  { key: "rankings", label: "Rankings", icon: Trophy },
];

export default function ProductionCenter() {
  const [options, setOptions] = useState(null);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [preset, setPreset] = useState("month");
  const [showFilters, setShowFilters] = useState(true);
  const [tab, setTab] = useState("overview");
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    api.get("/production-reports/filter-options")
      .then(({ data }) => setOptions(data))
      .catch(() => toast.error("Falha ao carregar opções de filtro"));
  }, []);

  useEffect(() => {
    let alive = true;
    setKpisLoading(true);
    api.get("/production-reports/kpis", { params: buildParams(applied) })
      .then(({ data }) => alive && setKpis(data))
      .catch(() => toast.error("Falha ao carregar KPIs"))
      .finally(() => alive && setKpisLoading(false));
    return () => { alive = false; };
  }, [applied]);

  const onApply = useCallback(() => {
    if (!draft.date_from || !draft.date_to) return toast.error("Escolha o período");
    if (draft.date_from > draft.date_to) return toast.error("Data inicial maior que a final");
    setApplied({ ...draft });
    toast.success("Filtros aplicados");
  }, [draft]);

  const onClear = useCallback(() => {
    const f = EMPTY_FILTERS();
    setDraft(f);
    setApplied(f);
    setPreset("month");
  }, []);

  const doExport = async (format) => {
    setExporting(format);
    try {
      const ext = format === "xlsx" ? "xlsx" : format;
      await downloadBlob("/production-reports/export",
        { ...buildParams(applied), format },
        `central_producao_${applied.date_from}_${applied.date_to}.${ext}`);
      toast.success(`Exportação ${format.toUpperCase()} concluída`);
    } catch {
      toast.error("Falha na exportação");
    } finally {
      setExporting(null);
    }
  };

  const activeFilterCount = ["collaborators", "sectors", "postos", "statuses", "types", "marketplaces", "stores", "states", "colors"]
    .reduce((a, k) => a + (applied[k]?.length || 0), 0) +
    ["skus", "cities", "order_number", "qty_min", "qty_max", "value_min", "value_max"]
      .reduce((a, k) => a + (applied[k] ? 1 : 0), 0);

  return (
    <div className="p-3 sm:p-4 space-y-3" data-testid="production-center-page">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <div className="overline text-[10px] text-zinc-500">RELATÓRIOS · BUSINESS INTELLIGENCE</div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight font-head flex items-center gap-2">
            Central de Produção
            <HintButton text="Painel completo de inteligência da fábrica: KPIs, produção por posto, por colaborador, por SKU, gráficos interativos, rankings e insights automáticos. Todos os números respeitam os filtros aplicados no painel lateral. 'Produzido' = pedido movido para Enviado no período." />
          </h1>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <button className={`btn btn-tight ${showFilters ? "!bg-black !text-white" : ""}`}
            onClick={() => setShowFilters(!showFilters)} data-testid="pc-toggle-filters">
            <FunnelSimple size={14} className="inline mr-1" />
            Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
          <button className="btn btn-tight" onClick={() => doExport("xlsx")} disabled={!!exporting} data-testid="pc-export-xlsx">
            <FileXls size={14} className="inline mr-1" />{exporting === "xlsx" ? "..." : "Excel"}
          </button>
          <button className="btn btn-tight" onClick={() => doExport("csv")} disabled={!!exporting} data-testid="pc-export-csv">
            <FileCsv size={14} className="inline mr-1" />{exporting === "csv" ? "..." : "CSV"}
          </button>
          <button className="btn btn-tight" onClick={() => doExport("pdf")} disabled={!!exporting} data-testid="pc-export-pdf">
            <FilePdf size={14} className="inline mr-1" />{exporting === "pdf" ? "..." : "PDF"}
          </button>
          <button className="btn btn-tight" onClick={() => doExport("json")} disabled={!!exporting} data-testid="pc-export-json">
            <FileCode size={14} className="inline mr-1" />{exporting === "json" ? "..." : "JSON"}
          </button>
          <button className="btn btn-tight" onClick={() => window.print()} data-testid="pc-print">
            <Printer size={14} className="inline mr-1" />Imprimir
          </button>
        </div>
      </div>

      <KpiGrid kpis={kpis} loading={kpisLoading} />

      <div className="flex flex-col lg:flex-row gap-3 items-start">
        {showFilters && (
          <FiltersSidebar
            options={options} draft={draft} setDraft={setDraft}
            preset={preset} setPreset={setPreset}
            onApply={onApply} onClear={onClear}
            onClose={() => setShowFilters(false)}
          />
        )}
        <div className="flex-1 min-w-0 w-full space-y-3">
          <div className="flex gap-1 overflow-x-auto pb-1" data-testid="pc-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded whitespace-nowrap transition-colors ${tab === t.key ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
                  data-testid={`pc-tab-${t.key}`}>
                  <Icon size={14} weight={tab === t.key ? "fill" : "regular"} />
                  {t.label}
                </button>
              );
            })}
          </div>
          {tab === "overview" && <OverviewTab applied={applied} />}
          {tab === "stations" && <StationsTab applied={applied} />}
          {tab === "collaborators" && <CollaboratorsTab applied={applied} />}
          {tab === "skus" && <SkusTab applied={applied} />}
          {tab === "rankings" && <RankingsTab applied={applied} />}
        </div>
      </div>
    </div>
  );
}
